/**
 * GapToolbar - Top toolbar for gap/silence removal mode.
 * Rule-based design: "Gaps longer than X → shorten to Y"
 * Compact layout matching FillerWordToolbar.
 * Follows Libretto's calm, editorial design system.
 */
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { 
    Box, 
    IconButton, 
    Button, 
    Typography,
    TextField,
    InputAdornment,
    CircularProgress,
} from '@mui/material';
import {
    Close as CloseIcon,
    KeyboardArrowUp as ArrowUpIcon,
    KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { GapStore } from './GapStore';
import { CompositionService } from '../../../librettoEditor/services/CompositionService';
import { ScriptLayerStore } from '../../../librettoEditor/store/ScriptLayerStore';

interface GapToolbarProps {
    onShortenCurrent: () => void;
    onShortenAll: () => void;
    onClose: () => void;
}

const GapToolbar: React.FC<GapToolbarProps> = observer(({
    onShortenCurrent,
    onShortenAll,
    onClose,
}) => {
    // Local state for text field values (allows typing before committing)
    const [minGapText, setMinGapText] = useState(String(GapStore.minGapThreshold));
    const [shortenToText, setShortenToText] = useState(String(GapStore.shortenToSeconds));
    
    // Sync local state when store changes externally
    useEffect(() => {
        setMinGapText(String(GapStore.minGapThreshold));
    }, [GapStore.minGapThreshold]);
    
    useEffect(() => {
        setShortenToText(String(GapStore.shortenToSeconds));
    }, [GapStore.shortenToSeconds]);
    
    const handleNavigate = (direction: 'prev' | 'next') => {
        if (direction === 'prev') {
            GapStore.goToPrevious();
        } else {
            GapStore.goToNext();
        }
        
        // Seek to the current gap - map source time through segments
        const current = GapStore.currentGap;
        if (current) {
            const compTime = ScriptLayerStore.mapSourceTimeToCompositionTime(current.startSec);
            if (compTime !== null) {
                CompositionService.seek(compTime);
            } else {
                CompositionService.seek(current.startSec);
            }
        }
    };
    
    // Commit min gap threshold on blur or Enter
    const commitMinGap = () => {
        const value = parseFloat(minGapText);
        if (!isNaN(value) && value >= 0) {
            void GapStore.setMinGapThreshold(value);
        } else {
            setMinGapText(String(GapStore.minGapThreshold));
        }
    };
    
    // Commit shorten-to value on blur or Enter
    const commitShortenTo = () => {
        const value = parseFloat(shortenToText);
        if (!isNaN(value) && value >= 0) {
            GapStore.setShortenToSeconds(value);
        } else {
            setShortenToText(String(GapStore.shortenToSeconds));
        }
    };
    
    const handleMinGapKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            commitMinGap();
            (e.target as HTMLInputElement).blur();
        }
    };
    
    const handleShortenToKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            commitShortenTo();
            (e.target as HTMLInputElement).blur();
        }
    };
    
    const isLoading = GapStore.isRemoving;
    const totalRemovedSec = GapStore.totalSecondsRemoved;
    const shortenableCount = GapStore.shortenableCount;

    const formatSavings = (seconds: number): string => {
        if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
        if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
        if (seconds < 60) return `${seconds.toFixed(1)}s`;
        let mins = Math.floor(seconds / 60);
        let secs = Math.round(seconds % 60);
        if (secs === 60) {
            mins += 1;
            secs = 0;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    
    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#fff',
                padding: '14px 16px',
                paddingRight: '40px', // Space for close button
                gap: 1.5,
                minHeight: 56,
            }}
        >
            {/* Close button - top right */}
            <IconButton 
                size="small" 
                onClick={onClose}
                sx={{ 
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    padding: '4px',
                    color: '#9CA3AF',
                    '&:hover': { backgroundColor: '#F3F4F6', color: '#374151' },
                }}
            >
                <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
            
            {/* Rule: "Gaps longer than X → shorten to Y" + summary */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Gaps longer than
                    </Typography>
                    <TextField
                        size="small"
                        type="text"
                        value={minGapText}
                        onChange={(e) => setMinGapText(e.target.value)}
                        onBlur={commitMinGap}
                        onKeyDown={handleMinGapKeyDown}
                        inputProps={{
                            style: { 
                                textAlign: 'center',
                                padding: '4px 6px',
                                width: '28px',
                                fontWeight: 500,
                                fontSize: 13,
                            },
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>s</Typography>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flexShrink: 0,
                            '& .MuiOutlinedInput-root': {
                                height: '28px',
                                borderRadius: '6px',
                                '& fieldset': { borderColor: '#E5E7EB' },
                                '&:hover fieldset': { borderColor: '#D1D5DB' },
                                '&.Mui-focused fieldset': { borderColor: '#2B6BFD', borderWidth: 1 },
                            },
                        }}
                    />

                    <Typography sx={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        shorten to
                    </Typography>
                    <TextField
                        size="small"
                        type="text"
                        value={shortenToText}
                        onChange={(e) => setShortenToText(e.target.value)}
                        onBlur={commitShortenTo}
                        onKeyDown={handleShortenToKeyDown}
                        inputProps={{
                            style: { 
                                textAlign: 'center',
                                padding: '4px 6px',
                                width: '28px',
                                fontWeight: 500,
                                fontSize: 13,
                            },
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>s</Typography>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flexShrink: 0,
                            '& .MuiOutlinedInput-root': {
                                height: '28px',
                                borderRadius: '6px',
                                '& fieldset': { borderColor: '#E5E7EB' },
                                '&:hover fieldset': { borderColor: '#D1D5DB' },
                                '&.Mui-focused fieldset': { borderColor: '#2B6BFD', borderWidth: 1 },
                            },
                        }}
                    />
                </Box>

                <Typography sx={{ fontSize: 12, color: '#9CA3AF', lineHeight: 1.2 }}>
                    Will remove {formatSavings(totalRemovedSec)}{shortenableCount > 0 ? ` across ${shortenableCount} gap${shortenableCount === 1 ? '' : 's'}` : ''}
                </Typography>
            </Box>
            
            {/* Counter and navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 50 }}>
                        <CircularProgress size={12} sx={{ color: '#9CA3AF' }} />
                        <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
                            {GapStore.removalProgress}%
                        </Typography>
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: 13, color: '#9CA3AF', fontWeight: 500, minWidth: 40 }}>
                        {GapStore.currentDisplayIndex} / {GapStore.totalCount}
                    </Typography>
                )}
                
                <IconButton 
                    size="small" 
                    onClick={() => handleNavigate('prev')}
                    disabled={GapStore.totalCount === 0 || isLoading}
                    sx={{ 
                        padding: '4px',
                        color: '#9CA3AF',
                        '&:hover': { backgroundColor: '#F3F4F6', color: '#374151' },
                        '&.Mui-disabled': { color: '#D1D5DB' },
                    }}
                >
                    <ArrowUpIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                    size="small" 
                    onClick={() => handleNavigate('next')}
                    disabled={GapStore.totalCount === 0 || isLoading}
                    sx={{ 
                        padding: '4px',
                        color: '#9CA3AF',
                        '&:hover': { backgroundColor: '#F3F4F6', color: '#374151' },
                        '&.Mui-disabled': { color: '#D1D5DB' },
                    }}
                >
                    <ArrowDownIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
            
            {/* Action buttons - stacked vertically */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flexShrink: 0 }}>
                <Button
                    variant="text"
                    size="small"
                    onClick={onShortenCurrent}
                    disabled={GapStore.totalCount === 0 || isLoading}
                    sx={{
                        color: '#6B7280',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        padding: '6px 14px',
                        borderRadius: '8px',
                        minWidth: 'auto',
                        lineHeight: 1.4,
                        '&:hover': {
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                        },
                        '&.Mui-disabled': { color: '#D1D5DB' },
                    }}
                >
                    Shorten
                </Button>
                <Button
                    data-tour="gap-shorten-all"
                    variant="text"
                    size="small"
                    onClick={onShortenAll}
                    disabled={GapStore.totalCount === 0 || isLoading}
                    sx={{
                        color: '#6B7280',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        padding: '6px 14px',
                        borderRadius: '8px',
                        minWidth: 'auto',
                        lineHeight: 1.4,
                        '&:hover': {
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                        },
                        '&.Mui-disabled': { color: '#D1D5DB' },
                    }}
                >
                    Shorten all ({GapStore.totalCount})
                </Button>
            </Box>
        </Box>
    );
});

export default GapToolbar;
